import { useEffect, useState } from "react";
import DeliveryFlowView from "./view";
import { noop, DELIVERY_TYPE } from "@client/shared/constants";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";

import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TeamSelector from "../team-selector";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import RingoutPrerecorder from "../ringout-prerecorder";

const defaultState = {
  showStepModal: false,
  isStepEditing: false,
  activeStep: "emails",
  activeStepValue: null,
  currentElement: null,
  activeStepRepeatId: 0,
  activeStepDelayUnit: "min",
  activeStepError: " ",
};

const ACTIONS_TYPES = [
  { label: "Email", type: "emails" },
  { label: "Webhook", type: "webhook" },
  { label: "Glip", type: "glip_teams" },
  { label: "SMS", type: "sms_to" },
  { label: "RingOut with recorded message", type: "ringout" },
  { label: "Delay", type: "delay" },
  { label: "Check: Voicemail Read", type: "voicemail-read-condition" },
  { label: "Repeat", type: "goto" },
  { label: "Stop", type: "stop" },
];

const DeliveryFlow = ({
  teams = [],
  users = [],
  deliverySteps = [],
  voicemailExtensions = {},
  countryList = [],
  onDeliveryStepsUpdate = noop,
  onWebhookOpen = noop,
  onDeliveryFlowReady = noop,
}) => {
  const [state, setState] = useState(defaultState);

  onDeliveryFlowReady({
    addWebhook: (webhook) => {
      setState((prevState) => ({
        ...prevState,
        activeStepValue: webhook,
      }));
    },
  });

  function getStepElement(elementId, elements) {
    return elements.find((element) => element.id === elementId);
  }

  function removeStaleElements(elements, startId) {
    const updatedElements = [];
    let nextId = startId;
    while (typeof nextId === "number") {
      const nextElement = getStepElement(nextId, elements);
      if (nextElement) {
        updatedElements.push({ ...nextElement });
        if (nextElement.type === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION) {
          const { gotoTrue, gotoFalse } = nextElement;
          if (typeof gotoTrue === "number") {
            updatedElements.push(...removeStaleElements(elements, gotoTrue));
          }

          if (typeof gotoFalse === "number") {
            updatedElements.push(...removeStaleElements(elements, gotoFalse));
          }

          nextId = null;
        } else {
          nextId = nextElement.goto;
        }
      }
    }

    return updatedElements;
  }

  const handleAddElement = ({ element, isTruePath }) => {
    const {
      activeStep: newStepType,
      activeStepValue: newStepValue,
      activeStepRepeatId: newStepRepeatStepId,
      activeStepDelayUnit: newStepDelayAmount,
    } = state;
    let deliveryStepClone = deliverySteps.slice();
    const newElementId = Date.now();
    let stepToAdd = {
      id: newElementId,
      type: newStepType,
      value:
        typeof newStepValue === "string"
          ? (newStepValue || "").trim()
          : newStepValue,
      ...(newStepType === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION
        ? {
            gotoTrue: "",
            gotoFalse: "",
          }
        : {
            goto: !!element
              ? element.type === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION
                ? isTruePath
                  ? element.gotoTrue || ""
                  : element.gotoFalse || ""
                : element.goto || ""
              : "",
          }),
    };

    if (state.activeStep === DELIVERY_TYPE.REPEAT) {
      stepToAdd.repeatGoto = newStepRepeatStepId;
    }
    if (state.activeStep === DELIVERY_TYPE.DELAY) {
      stepToAdd.delayUnit = newStepDelayAmount;
    }
    // Check if the passed element is not null and undefined.
    // Element should always contain a value if the element is not being added
    // at the very start.
    if (element) {
      const elementClone = getStepElement(element.id, deliveryStepClone);
      if (element.type === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION) {
        if (isTruePath) {
          elementClone.gotoTrue = newElementId;
        } else {
          elementClone.gotoFalse = newElementId;
        }
      } else {
        elementClone.goto = newElementId;
      }

      deliveryStepClone.push(stepToAdd);
    } else {
      if (
        deliveryStepClone.length > 0 &&
        stepToAdd.type !== DELIVERY_TYPE.VOICEMAIL_READ_CONDITION
      ) {
        stepToAdd.goto = deliveryStepClone[0].id;
      }

      deliveryStepClone.unshift(stepToAdd);
    }

    deliveryStepClone = removeStaleElements(
      deliveryStepClone,
      deliveryStepClone[0].id
    );
    onDeliveryStepsUpdate(deliveryStepClone);
  };

  const handleElementRemove = (removedElement) => {
    const removedElementId = removedElement.id;
    let deliveryStepClone = deliverySteps.slice();
    // Restrict to delete repeat assigned element
    const assignedIndicesForRepeat = deliveryStepClone
      .filter((item) => item.type === DELIVERY_TYPE.REPEAT)
      .map((item) => +item.repeatGoto);
    if (assignedIndicesForRepeat.includes(removedElementId)) {
      toast.error(
        "This step is assigned for repeat and cannot be deleted till the repeat step is deleted."
      );
      return false;
    }
    const previousElement = deliveryStepClone.find((element) => {
      if (element.type === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION) {
        return (
          element.gotoTrue === removedElementId ||
          element.gotoFalse === removedElementId
        );
      } else {
        return element.goto === removedElementId;
      }
    });

    if (previousElement) {
      if (previousElement.type === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION) {
        let previousElementKey = "";
        if (previousElement.gotoTrue === removedElementId) {
          previousElementKey = "gotoTrue";
        } else if (previousElement.gotoFalse === removedElementId) {
          previousElementKey = "gotoFalse";
        }
        if (removedElement.type === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION) {
          previousElement[previousElementKey] = "";
        } else {
          previousElement[previousElementKey] =
            removedElement.goto === null ? "" : removedElement.goto;
        }
      } else {
        if (removedElement.type === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION) {
          previousElement.goto = "";
        } else {
          previousElement.goto =
            removedElement.goto === null ? "" : removedElement.goto;
        }
      }
    } else {
      const removedElementIndex = deliveryStepClone.findIndex(
        (element) => element.id === removedElementId
      );
      if (removedElementIndex === 0) {
        if (removedElement.type === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION) {
          deliveryStepClone = [];
        } else {

        deliveryStepClone.splice(0, 1);
        }
      }
    }

    if (deliveryStepClone.length > 0) {
      deliveryStepClone = removeStaleElements(
        deliveryStepClone,
        deliveryStepClone[0].id
      );
    }

    onDeliveryStepsUpdate(deliveryStepClone);
  };

  /*  const handleElementRemove = (element) => {
    let deliveryClone = deliverySteps.slice();
    const elementIndex = element.positionIndex;
    if (elementIndex === 0) {
      if (element.type === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION) {
        deliveryClone = [];
      } else {
        deliveryClone[elementIndex].isDeleted = true;
      }
    } else {
      if (element.type === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION) {
        markPathForDeletion(deliveryClone, elementIndex);
        deliveryClone.forEach((item) => {
          if (item.goto === elementIndex) {
            item.goto = "";
          } else if (item.gotoTrue === elementIndex) {
            item.gotoTrue = "";
          } else if (item.gotoFalse === elementIndex) {
            item.gotoFalse = "";
          }
        });
      } else {
        deliveryClone[elementIndex].isDeleted = true;
        deliveryClone.forEach((item) => {
          if (item.goto === elementIndex) {
            item.goto = element.goto === null ? '' : element.goto;
          } else if (item.gotoTrue === elementIndex) {
            item.gotoTrue = element.goto === null ? '' : element.goto;
          } else if (item.gotoFalse === elementIndex) {
            item.gotoFalse = element.goto === null ? '' : element.goto;
          }
        });
      }
    }

    onDeliveryStepsUpdate(deliveryClone);
  }; */

  const renderStepTemplate = (step) => {
    let template = null;
    switch (step) {
      case DELIVERY_TYPE.EMAIL:
        template = (
          <TextField
            variant="outlined"
            label="Email"
            value={state.activeStepValue}
            onChange={(evt) => {
              const { value } = evt.currentTarget;
              setState((prevState) => ({
                ...prevState,
                activeStepValue: value,
              }));
            }}
          />
        );
        break;
      case DELIVERY_TYPE.SMS:
        template = (
          <TextField
            variant="outlined"
            label="Number"
            value={state.activeStepValue}
            onChange={(evt) => {
              const { value } = evt.currentTarget;
              setState((prevState) => ({
                ...prevState,
                activeStepValue: value,
              }));
            }}
          />
        );
        break;
      case DELIVERY_TYPE.DELAY:
        template = (
          <TextField
            type="number"
            variant="outlined"
            label="Delay"
            value={state.activeStepValue}
            error={!!(state.activeStepError || "").trim()}
            helperText={state.activeStepError}
            onChange={(evt) => {
              const { value } = evt.currentTarget;
              const isExceeded = value > 15;
              const isMinimum = value < 1;
              setState((prevState) => ({
                ...prevState,
                activeStepValue: value,
                activeStepError: isMinimum
                  ? "Minimum value is 1"
                  : isExceeded
                  ? "Maximum value is 15"
                  : null,
              }));
            }}
            InputProps={{
              endAdornment: (
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Minute(s)
                </Typography>
              ),
            }}
          />
        );
        break;
      case DELIVERY_TYPE.GLIP:
        template = (
          <Box sx={{ minWidth: 420, maxWidth: 420 }}>
            <TeamSelector
              label="Send a Glip message to the following teams:"
              teams={teams}
              value={state.activeStepValue}
              onChange={(ringCentralTeams) => {
                setState((prevState) => ({
                  ...prevState,
                  activeStepValue: ringCentralTeams,
                }));
              }}
              users={users}
            />
          </Box>
        );
        break;
      case DELIVERY_TYPE.WEBHOOK:
        template = (
          <>
            <Button
              sx={{ marginLeft: 4 }}
              color="primary"
              startIcon={<img alt="" src={"/assets/plus.svg"} />}
              onClick={() => onWebhookOpen(state.activeStepValue)}
              variant="outlined"
            >
              <Typography variant="button" noWrap>
                {state.isStepEditing ? "Edit Webhook" : "Add Webhook"}
              </Typography>
            </Button>
            {state.activeStepValue?.webhook && (
              <Typography variant="body1" sx={{ mt: 4 }}>
                Webhook: {state.activeStepValue?.webhook}
              </Typography>
            )}
          </>
        );
        break;
      case DELIVERY_TYPE.VOICEMAIL_READ_CONDITION:
        template = (
          <Typography variant="h6" noWrap>
            {`Check condition: Voicemail read (${
              voicemailExtensions?.extensionNumber || "-"
            })`}
          </Typography>
        );
        break;
      case DELIVERY_TYPE.REPEAT:
        const indexOfCurrentElement = deliverySteps.findIndex(
          (item) =>
            item.id ===
            (state.currentElement?.id || state.currentElement?.element?.id)
        );

        const currentElementId = state.currentElement?.id || state.currentElement?.element?.id;
        let deliveryStepsToBeAdd = [];
        let element = deliverySteps.find(item => item.id === currentElementId);
        if (element) {
          deliveryStepsToBeAdd.push(element);
        }
        while (element) {
          const previousElement = deliverySteps.find((elem) =>
            elem.type === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION
              ? elem.gotoTrue === element.id ||
                elem.gotoFalse === element.id 
              : elem.goto === element.id 
          );
          element = deliverySteps.find(
            (item) => item?.id === previousElement?.id
          );
          previousElement && deliveryStepsToBeAdd.push(previousElement);
        }
        deliveryStepsToBeAdd = deliveryStepsToBeAdd.reverse();

        template = (
          <Box>
            {indexOfCurrentElement === -1 && (
              <Alert sx={{ my: 2 }} severity="warning">
                There is no previous steps.
              </Alert>
            )}
            {indexOfCurrentElement > -1 && (
              <Box sx={{ my: 2 }}>
                <Typography variant="body1">Steps</Typography>
                <FormControl sx={{ mt: 2 }}>
                  <RadioGroup
                    value={state.activeStepRepeatId}
                    onChange={(evt) => {
                      const { value } = evt.target;
                      setState((prevState) => ({
                        ...prevState,
                        activeStepRepeatId: +value,
                      }));
                    }}
                  >
                    {deliveryStepsToBeAdd
                      // .filter((_, index) => index <= indexOfCurrentElement)
                      .map((item, ind) => {
                        const labelHeading =
                          ACTIONS_TYPES.find((elem) => item.type === elem?.type)
                            ?.label || "-";
                        let labelValue =
                          item.value?.webhook || item.value || "-";
                        if (item.type === DELIVERY_TYPE.RINGOUT) {
                          labelValue = `From Number-${item?.value?.phoneNumber}, To Number-${item?.value?.extensionPhoneNumber?.phoneNumber}`;
                        }

                        if (item.type === DELIVERY_TYPE.DELAY) {
                          labelValue = `${labelValue} Minute(s)`;
                        }
                        return (
                          <FormControlLabel
                            sx={{
                              display:
                                item.type === DELIVERY_TYPE.REPEAT
                                  ? "none"
                                  : "",
                            }}
                            value={item.id}
                            control={<Radio />}
                            label={`${labelHeading}: ${labelValue}`}
                          />
                        );
                      })}
                  </RadioGroup>
                </FormControl>
                <Box>
                  <Typography sx={{ my: 2 }} variant="body1">
                    Max Number Of Repeat
                  </Typography>
                  <TextField
                    type="number"
                    variant="outlined"
                    label="Repeat Times"
                    value={state.activeStepValue}
                    error={!!(state.activeStepError || "").trim()}
                    helperText={state.activeStepError}
                    onChange={(evt) => {
                      const { value } = evt.currentTarget;
                      setState((prevState) => ({
                        ...prevState,
                        activeStepValue: value,
                        activeStepError:
                          value < 1
                            ? "Minimum allowed value is 1"
                            : value > 5
                            ? "Maximum allowed value is 5"
                            : null,
                      }));
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        );
        break;
      case DELIVERY_TYPE.STOP:
        template = (
          <Typography variant="h6" noWrap>
            Stop flow
          </Typography>
        );
        break;
      case DELIVERY_TYPE.RINGOUT:
        template = (
          <Box sx={{ minWidth: 600, maxWidth: 600 }}>
            <RingoutPrerecorder
              ringout={state.activeStepValue}
              countryList={countryList}
              handleRingoutChange={(name, value) => {
                setState((prevState) => ({
                  ...prevState,
                  activeStepValue: {
                    ...(prevState.activeStepValue || {}),
                    [name]: value,
                  },
                }));
              }}
            />
          </Box>
        );
        break;
    }
    return template;
  };

  const isStepDialogValid = () => {
    let result = !!state.activeStepValue;
    if (
      state.activeStep === DELIVERY_TYPE.STOP ||
      state.activeStep === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION
    ) {
      return true;
    }

    if (typeof state.activeStepValue === "string") {
      result = !!state.activeStepValue.trim();
    }

    if (state.activeStep === DELIVERY_TYPE.RINGOUT) {
      result = !(
        !state.activeStepValue?.extension?.id ||
        !state.activeStepValue?.extensionPhoneNumber?.phoneNumber ||
        !state.activeStepValue?.phoneNumber ||
        (state.activeStepValue?.phoneNumberError || "").trim()
      );
    }

    if (state.activeStep === DELIVERY_TYPE.REPEAT) {
      result = state.activeStepRepeatId && result;
    }
    return result && !(state.activeStepError || "").trim();
  };

  return (
    <>
      <DeliveryFlowView
        ACTIONS_TYPES={ACTIONS_TYPES}
        delivery={deliverySteps}
        onAddElement={(currentElement) => {
          setState((prevState) => ({
            ...prevState,
            showStepModal: true,
            currentElement,
            activeStepRepeatId: 0,
            activeStepDelayUnit: "min",
          }));
        }}
        onElementClick={(currentElement) => {
          setState((prevState) => ({
            ...prevState,
            showStepModal: true,
            isStepEditing: true,
            currentElement,
            activeStep: currentElement?.type,
            activeStepValue: currentElement?.value,
            activeStepRepeatId: currentElement?.repeatGoto || 0,
            activeStepDelayUnit: currentElement?.delayUnit || "min",
          }));
        }}
        onElementRemove={handleElementRemove}
      />
      {state.showStepModal && (
        <Dialog
          open={state.showStepModal}
          sx={{
            "& .MuiDialog-paper": {
              maxWidth: "70%",
              width: "70%",
            },
          }}
        >
          <DialogContent
            sx={{ minHeight: "248px", minWidth: "100%", maxWidth: "100%" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h6">
                {state.isStepEditing ? "Edit Step" : "Add Step"}
              </Typography>
              <IconButton>
                <CloseIcon
                  sx={{ color: "grey.600" }}
                  fontSize="small"
                  onClick={() => {
                    setState((prevState) => ({
                      ...prevState,
                      activeStep: "email",
                      activeStepValue: null,
                      currentElement: null,
                      showStepModal: false,
                      isStepEditing: false,
                      activeStepError: " ",
                    }));
                  }}
                />
              </IconButton>
            </Box>

            <Box sx={{ my: 2 }}>
              <FormControl sx={{ mt: 2 }}>
                <RadioGroup
                  row
                  value={state.activeStep}
                  onChange={(evt) => {
                    const { value } = evt.target;
                    setState((prevState) => ({
                      ...prevState,
                      activeStep: value,
                      activeStepValue: "",
                      activeStepError: " ",
                    }));
                  }}
                >
                  {ACTIONS_TYPES.filter((item) =>
                    item.type === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION
                      ? !!Object.keys(voicemailExtensions || {}).length
                      : true
                  ).map((item) => (
                    <FormControlLabel
                      value={item.type}
                      control={<Radio disabled={state.isStepEditing} />}
                      label={item.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
            <Box sx={{ mt: 6 }}>{renderStepTemplate(state.activeStep)}</Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setState((prevState) => ({
                  ...prevState,
                  activeStep: "emails",
                  activeStepValue: null,
                  currentElement: null,
                  showStepModal: false,
                  isStepEditing: false,
                  activeStepError: " ",
                }));
              }}
              variant="text"
            >
              Cancel
            </Button>
            <Button
              disabled={!isStepDialogValid()}
              onClick={() => {
                if (state.isStepEditing) {
                  setState((prevState) => {
                    const updatedDelivery = deliverySteps.map((item) => {
                      if (item.id === prevState.currentElement?.id) {
                        item.type = prevState.activeStep;
                        item.value = prevState.activeStepValue;
                        if (item.type === DELIVERY_TYPE.REPEAT) {
                          item.repeatGoto = prevState.activeStepRepeatId;
                        }

                        if (item.type === DELIVERY_TYPE.DELAY) {
                          item.delayUnit = prevState.activeStepDelayUnit;
                        }
                      }
                      return item;
                    });
                    onDeliveryStepsUpdate(
                      updatedDelivery.map((item) => {
                        if (typeof item.value === "string") {
                          item.value = (item.value || "").trim();
                        }
                        return item;
                      })
                    );
                    return {
                      ...prevState,
                      activeStep: "emails",
                      activeStepValue: null,
                      currentElement: null,
                      showStepModal: false,
                      isStepEditing: false,
                      activeStepError: " ",
                    };
                  });
                } else {
                  if (state.activeStep !== DELIVERY_TYPE.STOP) {
                    handleAddElement(state.currentElement);
                  }
                  setState((prevState) => {
                    if (state.activeStep === DELIVERY_TYPE.STOP) {
                      const updatedDelivery = deliverySteps.map((item) => {
                        const isMatched =
                          item.id === prevState.currentElement?.element?.id;
                        if (isMatched) {
                          if (
                            item.type === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION
                          ) {
                            item[
                              prevState.currentElement.isTruePath
                                ? "gotoTrue"
                                : "gotoFalse"
                            ] = null;
                          } else {
                            item.goto = null;
                          }
                        }
                        return item;
                      });
                      onDeliveryStepsUpdate(updatedDelivery);
                    }
                    return {
                      ...prevState,
                      activeStep: "emails",
                      activeStepValue: null,
                      currentElement: null,
                      showStepModal: false,
                      isStepEditing: false,
                      activeStepError: " ",
                    };
                  });
                }
              }}
              variant="contained"
            >
              {state.isStepEditing ? "Edit" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default DeliveryFlow;
